'use client'

import { useState, useEffect, useMemo, useRef } from 'react'
import { Tooltip, Badge, Icon, Text } from '@/ui'
import { updateUserTransaction } from '@/featuresV1/Transactions/transactions.slice'
import { useAppDispatch } from '@/utils/typeHelpers'
import {
  TransactionOrSplit,
  TransactionType,
} from '@/reducers/admin/allTransactions.slice'
import { isFreeTrialPromoCode } from '@/selectors/user.selectors'
import { selectIsLockedTransaction } from '@/featuresV1/Transactions/transactions.selectors'
import { useReselector } from '@/utils/sharedHooks'
import { getTransactionCategorySelector } from '@/featuresV1/Reports/reports.selectors'
import { TransactionCategoryDropdown } from './TransactionCategoryDropdown'
import { fetchChangeLogsForUserTransaction } from '@/featuresV1/Admin/TransactionChangeLogs/transactionChangeLog.slice'
import { categoryLastChangedByBookkeeper } from '@/featuresV1/Transactions/helpers'
import RecategorizationModal from '@/featuresV1/Transactions/RecategorizationModal'
import { solid } from '@fortawesome/fontawesome-svg-core/import.macro'
import { EXPENSES_COLORS } from '@/src/constants/colorConstants'
import { TypographyStyles } from '@/styles'

interface Props {
  transaction: TransactionOrSplit
  editable?: boolean
  review?: boolean
  useRecategorizationModal?: boolean
  variant?: 'default' | 'text'
  filtered?: boolean
}

export const TransactionCategoryLabelReadOnly = ({
  transactionType,
  transactionCategoryId,
  variant = 'default',
}: {
  transactionType: TransactionType
  transactionCategoryId: number | null
} & Pick<Props, 'variant'>) => {
  const category = useReselector(
    getTransactionCategorySelector,
    transactionCategoryId
  )

  const colorByCategory =
    EXPENSES_COLORS[(transactionCategoryId || 0) % EXPENSES_COLORS.length]

  const text =
    transactionType === 'personal' ? 'N/A' : category?.name || 'Unselected'

  return (
    <span>
      {variant === 'default' ? (
        <Badge style={{ backgroundColor: colorByCategory }}>{text}</Badge>
      ) : (
        <span className="text-sm font-medium text-neutral-800">{text}</span>
      )}
    </span>
  )
}

export const TransactionCategoryLabel = ({
  transaction,
  editable,
  review,
  useRecategorizationModal = false,
  variant = 'default',
  filtered = false,
}: Props) => {
  const dispatch = useAppDispatch()
  const [editableDropdown, setEditableDropdown] = useState(review)
  const [updating, setUpdating] = useState(false)
  const [openModal, setOpenModal] = useState(false)
  const inputRef = useRef<HTMLInputElement | null>(null)

  const handleDropdownBlur = () => {
    // Delay to allow click on dropdown options before closing
    setTimeout(() => {
      setEditableDropdown(false)
    }, 150)
  }

  const hasFreeTrialPromoCode = useReselector(isFreeTrialPromoCode)
  const locked = useReselector(
    selectIsLockedTransaction,
    transaction.splitFrom || transaction.id
  )

  const [transactionCategoryId, setTransactionCategoryId] = useState<
    number | null
  >(null)
  const category = useReselector(
    getTransactionCategorySelector,
    transaction.transactionCategoryId
  )

  useEffect(() => {
    setTransactionCategoryId(transaction.transactionCategoryId)
  }, [transaction])

  const colorByCategory =
    EXPENSES_COLORS[
      (transaction.transactionCategoryId || 0) % EXPENSES_COLORS.length
    ]

  const text =
    transaction.type === 'personal' ? 'N/A' : category?.name || 'Unselected'

  const labelColor = useMemo(() => {
    if (text === 'Unselected') return 'text-neutral-500'
    if (category?.isTaxCompliant && transaction.type !== 'personal')
      return 'text-orange-500'
    return 'text-neutral-900'
  }, [category?.isTaxCompliant, text, transaction.type])

  const updateTransactionCategory = async (value: number | null) => {
    setUpdating(true)
    const updated = await updateUserTransaction(transaction.id, {
      transactionCategoryId: value,
    })(dispatch)
    if (updated) {
      setUpdating(false)
      setEditableDropdown(false)
    }
  }

  const confirmChange = async (value: number | null) => {
    setTransactionCategoryId(value)
    if (useRecategorizationModal) {
      const changeLogs = await fetchChangeLogsForUserTransaction({
        transactionId: transaction.id,
      })(dispatch)
      const wasBookkeeper = categoryLastChangedByBookkeeper(
        transaction.userId,
        changeLogs
      )
      if (wasBookkeeper) {
        setOpenModal(true)
        return
      }
    }
    updateTransactionCategory(value)
    return
  }

  const popupContent = useMemo(() => {
    if (hasFreeTrialPromoCode) return null
    return transaction.transactionCategoryId
      ? 'Did we miscategorize this transaction? Click to recategorize.'
      : 'No action needed right now! Our team reviews your transactions on a monthly basis and will categorize this for you.'
  }, [transaction.transactionCategoryId, hasFreeTrialPromoCode])

  const labelStyle = useMemo(() => {
    return variant === 'default'
      ? { backgroundColor: colorByCategory }
      : {
          color: labelColor,
          fontWeight: 500,
          fontSize: '0.875rem',
          padding: 0,
          display: 'flex',
          alignItems: 'center',
          gap: '0.25rem',
        }
  }, [variant, colorByCategory, labelColor])

  const showDropdown =
    editable && !locked && !transaction.type?.includes('personal')

  if (showDropdown) {
    return (
      <>
        {variant === 'text' && !review && !editableDropdown ? (
          <Tooltip open={locked || review ? false : undefined}>
            <Tooltip.Trigger asChild>
              <span
                className="cursor-pointer"
                role="button"
                tabIndex={0}
                onClick={() => setEditableDropdown(true)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    setEditableDropdown(true)
                  }
                }}
                style={labelStyle}
              >
                <Text
                  variant={TypographyStyles.BODY_MD}
                  style={{ marginBottom: 0 }}
                >
                  {text}
                </Text>
                <Icon icon={solid('caret-down')} className="ml-1 text-md" />
              </span>
            </Tooltip.Trigger>

            <Tooltip.Content
              side="bottom"
              className="max-w-[250px] bg-neutral-700 text-neutral-0 p-4 rounded-md"
            >
              {popupContent}
            </Tooltip.Content>
          </Tooltip>
        ) : null}

        {(editableDropdown || review) && (
          <TransactionCategoryDropdown
            placeholder="Search categories"
            optionStyle="normal"
            value={transactionCategoryId ?? undefined}
            transactionAmount={filtered ? transaction.amountInCents : undefined}
            transactionDate={transaction.date}
            onChange={(value) => confirmChange(value ?? null)}
            isTaxCompliant={
              variant === 'text' ? category?.isTaxCompliant : undefined
            }
            disabled={updating}
            inputRef={inputRef}
            onBlur={handleDropdownBlur}
          />
        )}

        <RecategorizationModal
          oldCategoryId={transaction.transactionCategoryId}
          newCategoryId={transactionCategoryId}
          open={openModal}
          close={() => {
            setOpenModal(false)
            setTransactionCategoryId(transaction.transactionCategoryId)
          }}
          updateAction={() => updateTransactionCategory(transactionCategoryId)}
        />
      </>
    )
  }

  return (
    <TransactionCategoryLabelReadOnly
      transactionType={transaction.type}
      transactionCategoryId={transaction.transactionCategoryId}
      variant={variant}
    />
  )
}
